import { useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useContentToken,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  AnimatePresence,
  motion,
  useScroll,
  useTransform,
} from 'framer-motion';
import {
  ArrowRight,
  FileText,
  Leaf,
  Minus,
  Plus,
  ShieldCheck,
} from 'lucide-react';
import { CountUp } from './CountUp';
import { plainText } from './propertyUtils';

const PILLAR_ACCENTS = ['#8fc7a8', '#549eff', '#4b6a8a'] as const;

const peopleImageDef: SlotGroupDefinition = {
  label: 'People Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'People' },
  },
};

const planetImageDef: SlotGroupDefinition = {
  label: 'Planet Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Planet' },
  },
};

const performanceImageDef: SlotGroupDefinition = {
  label: 'Performance Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Performance' },
  },
};

const CSR_2026_PDF =
  'https://irp.cdn-website.com/dac7e7d5/files/uploaded/ESG+Policy+Updated+2026+Jan.pdf';
const CSR_2025_PDF =
  'https://irp.cdn-website.com/dac7e7d5/files/uploaded/New+ALA+CSR+-+FINAL+2025.pdf';
const CSR_2024_PDF =
  'https://irp.cdn-website.com/dac7e7d5/files/uploaded/ALA+CSR+-+FINAL.pdf';
const CSR_2023_PDF =
  'https://irp.cdn-website.com/dac7e7d5/files/uploaded/ALA_Report_2024-2_FINAL.pdf';
const CSR_2022_PDF =
  'https://irp.cdn-website.com/dac7e7d5/files/uploaded/American%20Landmark%202022%20CSR%20Report.pdf';
const CSR_2021_PDF =
  'https://irp.cdn-website.com/dac7e7d5/files/uploaded/American+Landmark+2021+CSR+Report.pdf';
const ESG_POLICY_PDF =
  'https://irp.cdn-website.com/dac7e7d5/files/uploaded/ESG+Policy+Updated+5.8.2023+FINAL.pdf';
const SUSTAINABLE_LIVING_GUIDE_PDF =
  'https://irp.cdn-website.com/dac7e7d5/files/uploaded/Sustainable%20Living%20Guide.pdf';

const reportCtaDef: SlotGroupDefinition = {
  label: 'Latest Report CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue:
        'https://irp.cdn-website.com/dac7e7d5/files/uploaded/ESG+Policy+Updated+2026+Jan.pdf',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Read the 2026 Report' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const archive1Def: SlotGroupDefinition = {
  label: 'Archive Year 1',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue:
        'https://irp.cdn-website.com/dac7e7d5/files/uploaded/New+ALA+CSR+-+FINAL+2025.pdf',
    },
    label: { label: 'Year', type: 'text', defaultValue: '2025' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const archive2Def: SlotGroupDefinition = {
  label: 'Archive Year 2',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: 'https://irp.cdn-website.com/dac7e7d5/files/uploaded/ALA+CSR+-+FINAL.pdf',
    },
    label: { label: 'Year', type: 'text', defaultValue: '2024' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const archive3Def: SlotGroupDefinition = {
  label: 'Archive Year 3',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue:
        'https://irp.cdn-website.com/dac7e7d5/files/uploaded/ALA_Report_2024-2_FINAL.pdf',
    },
    label: { label: 'Year', type: 'text', defaultValue: '2023' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const archive4Def: SlotGroupDefinition = {
  label: 'Archive Year 4',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue:
        'https://irp.cdn-website.com/dac7e7d5/files/uploaded/American%20Landmark%202022%20CSR%20Report.pdf',
    },
    label: { label: 'Year', type: 'text', defaultValue: '2022' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const archive5Def: SlotGroupDefinition = {
  label: 'Archive Year 5',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue:
        'https://irp.cdn-website.com/dac7e7d5/files/uploaded/American+Landmark+2021+CSR+Report.pdf',
    },
    label: { label: 'Year', type: 'text', defaultValue: '2021' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const resource1Def: SlotGroupDefinition = {
  label: 'Resource 1',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue:
        'https://irp.cdn-website.com/dac7e7d5/files/uploaded/ESG+Policy+Updated+2026+Jan.pdf',
    },
    label: { label: 'CTA Text', type: 'text', defaultValue: 'View Reports' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const resource2Def: SlotGroupDefinition = {
  label: 'Resource 2',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue:
        'https://irp.cdn-website.com/dac7e7d5/files/uploaded/ESG+Policy+Updated+5.8.2023+FINAL.pdf',
    },
    label: { label: 'CTA Text', type: 'text', defaultValue: 'Read the Policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const resource3Def: SlotGroupDefinition = {
  label: 'Resource 3',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue:
        'https://irp.cdn-website.com/dac7e7d5/files/uploaded/Sustainable%20Living%20Guide.pdf',
    },
    label: { label: 'CTA Text', type: 'text', defaultValue: 'Open the Guide' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const communitiesCtaDef: SlotGroupDefinition = {
  label: 'Communities CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/our-communities' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Explore Our Communities' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const aboutCtaDef: SlotGroupDefinition = {
  label: 'About CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/about-us' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Learn About American Landmark' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

function splitTags(value: string): string[] {
  return value
    .split('|')
    .map((t) => t.trim())
    .filter(Boolean);
}

function EyebrowDot({ light = false }: { light?: boolean }) {
  return (
    <span
      className={`h-[9px] min-h-[9px] w-[9px] shrink-0 rounded-full ${light ? 'bg-[rgb(var(--ala-accent))]' : 'bg-[rgb(var(--ala-accent))]'}`}
      aria-hidden
    />
  );
}

function PillarTags({ items }: { items: string[] }) {
  return (
    <div className="mt-6 mb-20 flex flex-wrap content-start gap-2">
      {items.map((item) => (
        <span
          key={item}
          className="whitespace-nowrap rounded-full bg-[#7da9d6] px-4 py-2 font-heading text-sm font-medium text-[rgb(var(--ala-dark))]"
        >
          {item}
        </span>
      ))}
    </div>
  );
}

function MobilePillar({
  index,
  accent,
  labelPath,
  taglinePath,
  bodyPath,
  tagsPath,
  labelLabel,
  taglineLabel,
  bodyLabel,
  tagsLabel,
  defaultTags,
}: {
  index: number;
  accent: string;
  labelPath: string;
  taglinePath: string;
  bodyPath: string;
  tagsPath: string;
  labelLabel: string;
  taglineLabel: string;
  bodyLabel: string;
  tagsLabel: string;
  defaultTags: string;
}) {
  const [open, setOpen] = useState(false);
  const tags = splitTags(plainText(useContentToken(tagsPath)) || defaultTags);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="overflow-hidden rounded-lg border border-white/10 border-t-[3px] bg-[#0b141d]"
      style={{ borderTopColor: accent }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start justify-between gap-4 p-6 text-left"
      >
        <div>
          <span className="font-heading text-sm font-semibold uppercase tracking-[0.15em] text-white">
            {`0${index + 1}`}
          </span>
          <RichTextContent
            path={labelPath}
            label={labelLabel}
            as="h3"
            className="mt-2 font-heading text-[clamp(24px,6vw,32px)] font-semibold leading-tight text-[rgb(var(--ala-on-primary))]"
          />
          <RichTextContent
            path={taglinePath}
            label={taglineLabel}
            as="p"
            className="mt-1 font-heading text-base font-medium text-[rgb(var(--ala-on-primary))]/70"
          />
        </div>
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10">
          {open ? (
            <Minus className="h-5 w-5 text-[rgb(var(--ala-on-primary))]" />
          ) : (
            <Plus className="h-5 w-5 text-[rgb(var(--ala-on-primary))]" />
          )}
        </div>
      </button>
      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6">
              <RichTextContent
                path={bodyPath}
                label={bodyLabel}
                as="p"
                className="font-heading text-base font-normal leading-relaxed text-[rgb(var(--ala-on-primary))]/70"
              />
              <div className="mt-5 flex flex-wrap gap-2">
                {tags.map((item) => (
                  <span
                    key={item}
                    className="whitespace-nowrap rounded-full bg-[#7da9d6] px-4 py-2 font-heading text-sm font-medium text-[rgb(var(--ala-dark))]"
                  >
                    {item}
                  </span>
                ))}
              </div>
              <span className="sr-only">
                <RichTextContent path={tagsPath} label={tagsLabel} as="span" />
              </span>
            </div>
          </motion.div>
        ) : (
          <span className="sr-only">
            <RichTextContent path={bodyPath} label={bodyLabel} as="span" />
            <RichTextContent path={tagsPath} label={tagsLabel} as="span" />
          </span>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export function ImpactContent() {
  const [activePillar, setActivePillar] = useState(0);
  const timelineRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: timelineRef,
    offset: ['start center', 'end center'],
  });
  const progress = useTransform(scrollYProgress, [0, 1], [0, 1]);

  const stat1 = plainText(useContentToken('impactcontent.stat_1_value'));
  const stat2 = plainText(useContentToken('impactcontent.stat_2_value'));
  const stat3 = plainText(useContentToken('impactcontent.stat_3_value'));
  const envLabelText = plainText(useContentToken('impactcontent.env_label')) || 'Environmental';
  const socialLabelText = plainText(useContentToken('impactcontent.social_label')) || 'Social';
  const govLabelText = plainText(useContentToken('impactcontent.gov_label')) || 'Governance';
  const envTags = splitTags(
    plainText(useContentToken('impactcontent.env_tags')) ||
      'Energy efficiency|Water conservation|Solar initiatives|EV charging|Efficient appliances|Sustainable landscaping|Resiliency',
  );
  const socialTags = splitTags(
    plainText(useContentToken('impactcontent.social_tags')) ||
      'Resident wellness|Community engagement|Artist in Residence|Educator in Residence|Diversity & inclusion|Professional development|Philanthropy|Housing accessibility',
  );
  const govTags = splitTags(
    plainText(useContentToken('impactcontent.gov_tags')) ||
      'ESG governance|Leadership oversight|Ethics|Transparency|Risk management|ESG KPIs|Human rights|Responsible investment',
  );

  const peopleImage = useSlotGroup('impactcontent.people_image', peopleImageDef);
  const planetImage = useSlotGroup('impactcontent.planet_image', planetImageDef);
  const performanceImage = useSlotGroup('impactcontent.performance_image', performanceImageDef);
  const reportCta = useSlotGroup('impactcontent.report_cta', reportCtaDef);
  const archive1 = useSlotGroup('impactcontent.archive_1', archive1Def);
  const archive2 = useSlotGroup('impactcontent.archive_2', archive2Def);
  const archive3 = useSlotGroup('impactcontent.archive_3', archive3Def);
  const archive4 = useSlotGroup('impactcontent.archive_4', archive4Def);
  const archive5 = useSlotGroup('impactcontent.archive_5', archive5Def);
  const resource1 = useSlotGroup('impactcontent.resource_1_cta', resource1Def);
  const resource2 = useSlotGroup('impactcontent.resource_2_cta', resource2Def);
  const resource3 = useSlotGroup('impactcontent.resource_3_cta', resource3Def);
  const communitiesCta = useSlotGroup('impactcontent.communities_cta', communitiesCtaDef);
  const aboutCta = useSlotGroup('impactcontent.about_cta', aboutCtaDef);

  const pillars = [
    {
      key: 'environmental',
      accent: PILLAR_ACCENTS[0],
      spineLabel: envLabelText,
      labelPath: 'impactcontent.env_label',
      taglinePath: 'impactcontent.env_tagline',
      bodyPath: 'impactcontent.env_body',
      tagsPath: 'impactcontent.env_tags',
      tags: envTags,
      labelLabel: 'Environmental Label',
      taglineLabel: 'Environmental Tagline',
      bodyLabel: 'Environmental Body',
      tagsLabel: 'Environmental Tags',
      defaultTags:
        'Energy efficiency|Water conservation|Solar initiatives|EV charging|Efficient appliances|Sustainable landscaping|Resiliency',
    },
    {
      key: 'social',
      accent: PILLAR_ACCENTS[1],
      spineLabel: socialLabelText,
      labelPath: 'impactcontent.social_label',
      taglinePath: 'impactcontent.social_tagline',
      bodyPath: 'impactcontent.social_body',
      tagsPath: 'impactcontent.social_tags',
      tags: socialTags,
      labelLabel: 'Social Label',
      taglineLabel: 'Social Tagline',
      bodyLabel: 'Social Body',
      tagsLabel: 'Social Tags',
      defaultTags:
        'Resident wellness|Community engagement|Artist in Residence|Educator in Residence|Diversity & inclusion|Professional development|Philanthropy|Housing accessibility',
    },
    {
      key: 'governance',
      accent: PILLAR_ACCENTS[2],
      spineLabel: govLabelText,
      labelPath: 'impactcontent.gov_label',
      taglinePath: 'impactcontent.gov_tagline',
      bodyPath: 'impactcontent.gov_body',
      tagsPath: 'impactcontent.gov_tags',
      tags: govTags,
      labelLabel: 'Governance Label',
      taglineLabel: 'Governance Tagline',
      bodyLabel: 'Governance Body',
      tagsLabel: 'Governance Tags',
      defaultTags:
        'ESG governance|Leadership oversight|Ethics|Transparency|Risk management|ESG KPIs|Human rights|Responsible investment',
    },
  ] as const;

  return (
    <>
      {/* Impact at a glance */}
      <section className="w-full overflow-clip bg-[rgb(var(--ala-background))] py-20 md:py-28">
        <div className="mx-auto max-w-[1440px] px-6 md:px-[clamp(16px,6vw,87px)]">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mb-12 max-w-[760px] md:mb-16"
          >
            <div className="mb-4 flex items-center gap-3">
              <EyebrowDot />
              <RichTextContent
                path="impactcontent.stats_eyebrow"
                label="Stats Eyebrow"
                as="span"
                className="font-heading text-base font-medium uppercase tracking-[2px] text-[rgb(var(--ala-dark))]/60"
              />
            </div>
            <RichTextContent
              path="impactcontent.stats_headline"
              label="Stats Headline"
              as="h2"
              className="font-heading text-[clamp(28px,3.2vw,48px)] font-medium leading-[1.1] tracking-[-0.03em] text-[rgb(var(--ala-dark))]"
            />
          </motion.div>

          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-[#d5d5d5] bg-[#d5d5d5] sm:grid-cols-2 lg:grid-cols-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0 }}
              className="flex min-h-[260px] flex-col items-center justify-between bg-[rgb(var(--ala-background))] p-8 text-center transition-colors hover:bg-[rgb(var(--ala-secondary))]/[0.04] md:p-10"
            >
              <span className="sr-only">
                <RichTextContent path="impactcontent.stat_1_value" label="Stat 1 Value" as="span" />
              </span>
              <CountUp
                value={stat1 || '32+'}
                className="w-full font-heading text-[clamp(36px,4.2vw,60px)] font-semibold leading-[0.95] tracking-[-0.03em] text-[rgb(var(--ala-accent))]"
              />
              <RichTextContent
                path="impactcontent.stat_1_label"
                label="Stat 1 Label"
                as="p"
                className="mt-6 w-full font-heading text-base font-medium leading-[22px] text-[rgb(var(--ala-dark))]/70"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="flex min-h-[260px] flex-col items-center justify-between bg-[rgb(var(--ala-background))] p-8 text-center transition-colors hover:bg-[rgb(var(--ala-secondary))]/[0.04] md:p-10"
            >
              <span className="sr-only">
                <RichTextContent path="impactcontent.stat_2_value" label="Stat 2 Value" as="span" />
              </span>
              <CountUp
                value={stat2 || '3,000+'}
                className="w-full font-heading text-[clamp(36px,4.2vw,60px)] font-semibold leading-[0.95] tracking-[-0.03em] text-[rgb(var(--ala-accent))]"
              />
              <RichTextContent
                path="impactcontent.stat_2_label"
                label="Stat 2 Label"
                as="p"
                className="mt-6 w-full font-heading text-base font-medium leading-[22px] text-[rgb(var(--ala-dark))]/70"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex min-h-[260px] flex-col items-center justify-between bg-[rgb(var(--ala-background))] p-8 text-center transition-colors hover:bg-[rgb(var(--ala-secondary))]/[0.04] md:p-10"
            >
              <span className="sr-only">
                <RichTextContent path="impactcontent.stat_3_value" label="Stat 3 Value" as="span" />
              </span>
              <CountUp
                value={stat3 || '5'}
                className="w-full font-heading text-[clamp(36px,4.2vw,60px)] font-semibold leading-[0.95] tracking-[-0.03em] text-[rgb(var(--ala-accent))]"
              />
              <RichTextContent
                path="impactcontent.stat_3_label"
                label="Stat 3 Label"
                as="p"
                className="mt-6 w-full font-heading text-base font-medium leading-[22px] text-[rgb(var(--ala-dark))]/70"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex min-h-[260px] flex-col items-center justify-between bg-[rgb(var(--ala-background))] p-8 text-center transition-colors hover:bg-[rgb(var(--ala-secondary))]/[0.04] md:p-10"
            >
              <RichTextContent
                path="impactcontent.stat_4_value"
                label="Stat 4 Value"
                as="span"
                className="w-full font-heading text-[clamp(36px,4.2vw,60px)] font-semibold leading-[0.95] tracking-[-0.03em] text-[rgb(var(--ala-accent))]"
              />
              <RichTextContent
                path="impactcontent.stat_4_label"
                label="Stat 4 Label"
                as="p"
                className="mt-6 w-full font-heading text-base font-medium leading-[22px] text-[rgb(var(--ala-dark))]/70"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ESG pillars */}
      <section className="w-full overflow-clip bg-[rgb(var(--ala-dark))] py-20 md:py-32">
        <div className="mx-auto max-w-[1440px] px-6 md:px-[clamp(16px,6vw,87px)]">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mx-auto mb-12 flex max-w-[820px] flex-col items-center text-center md:mb-16"
          >
            <div className="mb-4 flex items-center gap-3">
              <EyebrowDot light />
              <RichTextContent
                path="impactcontent.esg_eyebrow"
                label="ESG Eyebrow"
                as="span"
                className="font-heading text-base font-medium uppercase tracking-[2px] text-[rgb(var(--ala-on-primary))]/60"
              />
            </div>
            <RichTextContent
              path="impactcontent.esg_headline"
              label="ESG Headline"
              as="h2"
              className="font-heading text-[clamp(28px,3.2vw,48px)] font-medium leading-[1.1] tracking-[-0.03em] text-[rgb(var(--ala-on-primary))]"
            />
            <RichTextContent
              path="impactcontent.esg_body"
              label="ESG Body"
              as="p"
              className="mt-5 font-heading text-[clamp(16px,1.3vw,20px)] font-normal leading-[1.55] text-[rgb(var(--ala-on-primary))]/70"
            />
          </motion.div>

          <div className="hidden h-[560px] gap-3 lg:flex">
            {pillars.map((pillar, index) => {
              const active = activePillar === index;
              return (
                <button
                  key={pillar.key}
                  type="button"
                  onClick={() => setActivePillar(index)}
                  onMouseEnter={() => setActivePillar(index)}
                  style={{
                    flex: active ? '1 1 0' : '0 0 96px',
                    borderTopColor: pillar.accent,
                  }}
                  className="relative h-full overflow-hidden rounded-lg border border-t-[3px] border-white/10 bg-[#0b141d] text-left transition-[flex] duration-500 ease-in-out"
                >
                  <AnimatePresence mode="wait">
                    {active ? (
                      <motion.div
                        key="active"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="absolute inset-0 flex flex-col px-8 py-20 md:p-10"
                      >
                        <span className="font-heading text-[clamp(40px,4vw,64px)] font-semibold leading-none text-white">
                          {`0${index + 1}`}
                        </span>
                        <div
                          className="mt-4 font-heading text-sm font-semibold uppercase tracking-[0.2em]"
                          style={{ color: pillar.accent }}
                        >
                          <RichTextContent
                            path={pillar.labelPath}
                            label={pillar.labelLabel}
                            as="span"
                          />
                        </div>
                        <RichTextContent
                          path={pillar.taglinePath}
                          label={pillar.taglineLabel}
                          as="h3"
                          className="mt-3 font-heading text-[clamp(26px,2.4vw,38px)] font-semibold leading-[1.05] tracking-[-0.02em] text-[rgb(var(--ala-on-primary))]"
                        />
                        <RichTextContent
                          path={pillar.bodyPath}
                          label={pillar.bodyLabel}
                          as="p"
                          className="mt-4 max-w-[680px] font-heading text-base font-normal leading-relaxed text-[rgb(var(--ala-on-primary))]/70"
                        />
                        <PillarTags items={[...pillar.tags]} />
                        <span className="sr-only">
                          <RichTextContent
                            path={pillar.tagsPath}
                            label={pillar.tagsLabel}
                            as="span"
                          />
                        </span>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="spine"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="absolute inset-0 flex flex-col items-center justify-between py-8"
                      >
                        <span className="font-heading text-sm font-semibold uppercase tracking-[0.15em] text-white">
                          {`0${index + 1}`}
                        </span>
                        <span className="whitespace-nowrap font-heading text-base font-semibold uppercase tracking-[0.25em] text-white [writing-mode:vertical-rl] rotate-180">
                          {pillar.spineLabel}
                        </span>
                        <span
                          className="h-12 w-[3px] rounded-full"
                          style={{ backgroundColor: pillar.accent }}
                        />
                        <span className="sr-only">
                          <RichTextContent
                            path={pillar.labelPath}
                            label={pillar.labelLabel}
                            as="span"
                          />
                          <RichTextContent
                            path={pillar.taglinePath}
                            label={pillar.taglineLabel}
                            as="span"
                          />
                          <RichTextContent
                            path={pillar.bodyPath}
                            label={pillar.bodyLabel}
                            as="span"
                          />
                          <RichTextContent
                            path={pillar.tagsPath}
                            label={pillar.tagsLabel}
                            as="span"
                          />
                        </span>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </button>
              );
            })}
          </div>

          <div className="flex flex-col gap-4 lg:hidden">
            {pillars.map((pillar, index) => (
              <MobilePillar
                key={pillar.key}
                index={index}
                accent={pillar.accent}
                labelPath={pillar.labelPath}
                taglinePath={pillar.taglinePath}
                bodyPath={pillar.bodyPath}
                tagsPath={pillar.tagsPath}
                labelLabel={pillar.labelLabel}
                taglineLabel={pillar.taglineLabel}
                bodyLabel={pillar.bodyLabel}
                tagsLabel={pillar.tagsLabel}
                defaultTags={pillar.defaultTags}
              />
            ))}
          </div>
        </div>
      </section>

      {/* People / Planet / Performance */}
      <section className="w-full overflow-clip bg-[rgb(var(--ala-dark))] py-20 md:py-32">
        <div className="mx-auto max-w-[1440px] px-6 md:px-[clamp(16px,6vw,87px)]">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mb-12 max-w-[720px] md:mb-16"
          >
            <div className="mb-4 flex items-center gap-3">
              <EyebrowDot light />
              <RichTextContent
                path="impactcontent.focus_eyebrow"
                label="Focus Eyebrow"
                as="span"
                className="font-heading text-base font-medium uppercase tracking-[2px] text-[rgb(var(--ala-on-primary))]/70"
              />
            </div>
            <RichTextContent
              path="impactcontent.focus_headline"
              label="Focus Headline"
              as="h2"
              className="font-heading text-[clamp(28px,3.2vw,48px)] font-medium leading-[1.1] tracking-[-0.03em] text-[rgb(var(--ala-on-primary))]"
            />
          </motion.div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <EditableSlotGroup groupPath="impactcontent.people_image" definition={peopleImageDef}>
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0 }}
                className="group relative flex min-h-[560px] flex-col overflow-clip rounded-lg"
              >
                <div className="relative flex-1 overflow-clip">
                  <VibesitesImage
                    url={
                      peopleImage.image ||
                      ''
                    }
                    alt={peopleImage.alt || ''}
                    sizes="(min-width: 1024px) 33vw, 100vw"
                    className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-[1.2s] ease-out group-hover:scale-110"
                  />
                  <div
                    className="absolute inset-0"
                    style={{
                      background:
                        'linear-gradient(180deg, rgb(var(--ala-dark)/0.20) 0%, rgb(var(--ala-dark)/0.96) 100%)',
                    }}
                  />
                </div>
                <div className="absolute inset-x-0 bottom-0 flex flex-col gap-3 p-8">
                  <RichTextContent
                    path="impactcontent.people_label"
                    label="People Label"
                    as="span"
                    className="font-heading text-sm font-semibold uppercase tracking-[0.2em] text-[rgb(var(--ala-accent))]"
                  />
                  <RichTextContent
                    path="impactcontent.people_headline"
                    label="People Headline"
                    as="h3"
                    className="font-heading text-[clamp(26px,2.6vw,40px)] font-semibold leading-[1.08] tracking-[-0.02em] text-[rgb(var(--ala-on-primary))]"
                  />
                  <RichTextContent
                    path="impactcontent.people_body"
                    label="People Body"
                    as="p"
                    className="max-w-[360px] font-heading text-base font-normal leading-relaxed text-[rgb(var(--ala-on-primary))]/70"
                  />
                </div>
              </motion.div>
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="impactcontent.planet_image" definition={planetImageDef}>
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.15 }}
                className="group relative flex min-h-[560px] flex-col overflow-clip rounded-lg"
              >
                <div className="relative flex-1 overflow-clip">
                  <VibesitesImage
                    url={
                      planetImage.image ||
                      ''
                    }
                    alt={planetImage.alt || ''}
                    sizes="(min-width: 1024px) 33vw, 100vw"
                    className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-[1.2s] ease-out group-hover:scale-110"
                  />
                  <div
                    className="absolute inset-0"
                    style={{
                      background:
                        'linear-gradient(180deg, rgb(var(--ala-dark)/0.20) 0%, rgb(var(--ala-dark)/0.96) 100%)',
                    }}
                  />
                </div>
                <div className="absolute inset-x-0 bottom-0 flex flex-col gap-3 p-8">
                  <RichTextContent
                    path="impactcontent.planet_label"
                    label="Planet Label"
                    as="span"
                    className="font-heading text-sm font-semibold uppercase tracking-[0.2em] text-[rgb(var(--ala-accent))]"
                  />
                  <RichTextContent
                    path="impactcontent.planet_headline"
                    label="Planet Headline"
                    as="h3"
                    className="font-heading text-[clamp(26px,2.6vw,40px)] font-semibold leading-[1.08] tracking-[-0.02em] text-[rgb(var(--ala-on-primary))]"
                  />
                  <RichTextContent
                    path="impactcontent.planet_body"
                    label="Planet Body"
                    as="p"
                    className="max-w-[360px] font-heading text-base font-normal leading-relaxed text-[rgb(var(--ala-on-primary))]/70"
                  />
                </div>
              </motion.div>
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="impactcontent.performance_image" definition={performanceImageDef}>
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.3 }}
                className="group relative flex min-h-[560px] flex-col overflow-clip rounded-lg"
              >
                <div className="relative flex-1 overflow-clip">
                  <VibesitesImage
                    url={
                      performanceImage.image ||
                      ''
                    }
                    alt={performanceImage.alt || ''}
                    sizes="(min-width: 1024px) 33vw, 100vw"
                    className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-[1.2s] ease-out group-hover:scale-110"
                  />
                  <div
                    className="absolute inset-0"
                    style={{
                      background:
                        'linear-gradient(180deg, rgb(var(--ala-dark)/0.20) 0%, rgb(var(--ala-dark)/0.96) 100%)',
                    }}
                  />
                </div>
                <div className="absolute inset-x-0 bottom-0 flex flex-col gap-3 p-8">
                  <RichTextContent
                    path="impactcontent.performance_label"
                    label="Performance Label"
                    as="span"
                    className="font-heading text-sm font-semibold uppercase tracking-[0.2em] text-[rgb(var(--ala-accent))]"
                  />
                  <RichTextContent
                    path="impactcontent.performance_headline"
                    label="Performance Headline"
                    as="h3"
                    className="font-heading text-[clamp(26px,2.6vw,40px)] font-semibold leading-[1.08] tracking-[-0.02em] text-[rgb(var(--ala-on-primary))]"
                  />
                  <RichTextContent
                    path="impactcontent.performance_body"
                    label="Performance Body"
                    as="p"
                    className="max-w-[360px] font-heading text-base font-normal leading-relaxed text-[rgb(var(--ala-on-primary))]/70"
                  />
                </div>
              </motion.div>
            </EditableSlotGroup>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="w-full overflow-clip bg-[#F7F7F7] py-20 md:py-32">
        <div className="mx-auto max-w-[1280px] px-6 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mb-12 max-w-[720px] md:mb-16"
          >
            <div className="mb-4 flex items-center gap-3">
              <EyebrowDot />
              <RichTextContent
                path="impactcontent.timeline_eyebrow"
                label="Timeline Eyebrow"
                as="span"
                className="font-heading text-base font-medium uppercase tracking-[2px] text-[rgb(var(--ala-dark))]/60"
              />
            </div>
            <RichTextContent
              path="impactcontent.timeline_headline"
              label="Timeline Headline"
              as="h2"
              className="font-heading text-[clamp(28px,3.2vw,48px)] font-medium leading-[1.1] tracking-[-0.03em] text-[rgb(var(--ala-dark))]"
            />
            <RichTextContent
              path="impactcontent.timeline_body"
              label="Timeline Body"
              as="p"
              className="mt-5 font-heading text-[clamp(16px,1.3vw,20px)] font-normal leading-[1.55] text-[rgb(var(--ala-dark))]/70"
            />
          </motion.div>

          <div ref={timelineRef} className="relative hidden md:block">
            <div className="relative pb-4">
              <div className="absolute left-0 right-0 top-3 h-[2px] bg-[#d5d5d5]" />
              <motion.div
                style={{ scaleX: progress }}
                className="absolute left-0 top-3 h-[2px] origin-left bg-[rgb(var(--ala-accent))]"
              />
              <div className="relative flex justify-between gap-4">
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.6, delay: 0 }}
                  className="flex flex-1 flex-col items-start gap-4"
                >
                  <div className="h-6 w-6 rounded-full border-[3px] border-[rgb(var(--ala-accent))] bg-[rgb(var(--ala-background))] shadow-md" />
                  <RichTextContent
                    path="impactcontent.timeline_1_year"
                    label="Timeline 1 Year"
                    as="span"
                    className="font-heading text-sm font-semibold uppercase tracking-[0.12em] text-[rgb(var(--ala-accent))]"
                  />
                  <RichTextContent
                    path="impactcontent.timeline_1_title"
                    label="Timeline 1 Title"
                    as="h3"
                    className="font-heading text-[clamp(17px,1.4vw,22px)] font-semibold leading-tight tracking-[-0.02em] text-[rgb(var(--ala-dark))]"
                  />
                  <RichTextContent
                    path="impactcontent.timeline_1_body"
                    label="Timeline 1 Body"
                    as="p"
                    className="max-w-[220px] font-heading text-sm font-normal leading-relaxed text-[rgb(var(--ala-dark))]/70"
                  />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="flex flex-1 flex-col items-start gap-4"
                >
                  <div className="h-6 w-6 rounded-full border-[3px] border-[rgb(var(--ala-accent))] bg-[rgb(var(--ala-background))] shadow-md" />
                  <RichTextContent
                    path="impactcontent.timeline_2_year"
                    label="Timeline 2 Year"
                    as="span"
                    className="font-heading text-sm font-semibold uppercase tracking-[0.12em] text-[rgb(var(--ala-accent))]"
                  />
                  <RichTextContent
                    path="impactcontent.timeline_2_title"
                    label="Timeline 2 Title"
                    as="h3"
                    className="font-heading text-[clamp(17px,1.4vw,22px)] font-semibold leading-tight tracking-[-0.02em] text-[rgb(var(--ala-dark))]"
                  />
                  <RichTextContent
                    path="impactcontent.timeline_2_body"
                    label="Timeline 2 Body"
                    as="p"
                    className="max-w-[220px] font-heading text-sm font-normal leading-relaxed text-[rgb(var(--ala-dark))]/70"
                  />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="flex flex-1 flex-col items-start gap-4"
                >
                  <div className="h-6 w-6 rounded-full border-[3px] border-[rgb(var(--ala-accent))] bg-[rgb(var(--ala-background))] shadow-md" />
                  <RichTextContent
                    path="impactcontent.timeline_3_year"
                    label="Timeline 3 Year"
                    as="span"
                    className="font-heading text-sm font-semibold uppercase tracking-[0.12em] text-[rgb(var(--ala-accent))]"
                  />
                  <RichTextContent
                    path="impactcontent.timeline_3_title"
                    label="Timeline 3 Title"
                    as="h3"
                    className="font-heading text-[clamp(17px,1.4vw,22px)] font-semibold leading-tight tracking-[-0.02em] text-[rgb(var(--ala-dark))]"
                  />
                  <RichTextContent
                    path="impactcontent.timeline_3_body"
                    label="Timeline 3 Body"
                    as="p"
                    className="max-w-[220px] font-heading text-sm font-normal leading-relaxed text-[rgb(var(--ala-dark))]/70"
                  />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.6, delay: 0.30000000000000004 }}
                  className="flex flex-1 flex-col items-start gap-4"
                >
                  <div className="h-6 w-6 rounded-full border-[3px] border-[rgb(var(--ala-accent))] bg-[rgb(var(--ala-background))] shadow-md" />
                  <RichTextContent
                    path="impactcontent.timeline_4_year"
                    label="Timeline 4 Year"
                    as="span"
                    className="font-heading text-sm font-semibold uppercase tracking-[0.12em] text-[rgb(var(--ala-accent))]"
                  />
                  <RichTextContent
                    path="impactcontent.timeline_4_title"
                    label="Timeline 4 Title"
                    as="h3"
                    className="font-heading text-[clamp(17px,1.4vw,22px)] font-semibold leading-tight tracking-[-0.02em] text-[rgb(var(--ala-dark))]"
                  />
                  <RichTextContent
                    path="impactcontent.timeline_4_body"
                    label="Timeline 4 Body"
                    as="p"
                    className="max-w-[220px] font-heading text-sm font-normal leading-relaxed text-[rgb(var(--ala-dark))]/70"
                  />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="flex flex-1 flex-col items-start gap-4"
                >
                  <div className="h-6 w-6 rounded-full border-[3px] border-[rgb(var(--ala-accent))] bg-[rgb(var(--ala-background))] shadow-md" />
                  <RichTextContent
                    path="impactcontent.timeline_5_year"
                    label="Timeline 5 Year"
                    as="span"
                    className="font-heading text-sm font-semibold uppercase tracking-[0.12em] text-[rgb(var(--ala-accent))]"
                  />
                  <RichTextContent
                    path="impactcontent.timeline_5_title"
                    label="Timeline 5 Title"
                    as="h3"
                    className="font-heading text-[clamp(17px,1.4vw,22px)] font-semibold leading-tight tracking-[-0.02em] text-[rgb(var(--ala-dark))]"
                  />
                  <RichTextContent
                    path="impactcontent.timeline_5_body"
                    label="Timeline 5 Body"
                    as="p"
                    className="max-w-[220px] font-heading text-sm font-normal leading-relaxed text-[rgb(var(--ala-dark))]/70"
                  />
                </motion.div>
              </div>
            </div>
          </div>

          <div className="relative pl-8 md:hidden">
            <div className="absolute bottom-2 left-[7px] top-2 w-[2px] bg-[#d5d5d5]" />
            <motion.div
              style={{ scaleY: progress }}
              className="absolute bottom-2 left-[7px] top-2 w-[2px] origin-top bg-[rgb(var(--ala-accent))]"
            />
                        <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5 }}
              className="relative flex flex-col gap-2 pb-10"
            >
              <div className="absolute -left-8 top-1.5 z-20">
                <div className="h-4 w-4 rounded-full border-[3px] border-[rgb(var(--ala-accent))] bg-[rgb(var(--ala-background))] shadow-md" />
              </div>
              <RichTextContent
                path="impactcontent.timeline_1_year"
                label="Timeline 1 Year"
                as="span"
                className="font-heading text-sm font-semibold uppercase tracking-[0.12em] text-[rgb(var(--ala-accent))]"
              />
              <RichTextContent
                path="impactcontent.timeline_1_title"
                label="Timeline 1 Title"
                as="h3"
                className="font-heading text-[clamp(19px,5vw,24px)] font-semibold leading-tight tracking-[-0.02em] text-[rgb(var(--ala-dark))]"
              />
              <RichTextContent
                path="impactcontent.timeline_1_body"
                label="Timeline 1 Body"
                as="p"
                className="max-w-[420px] font-heading text-base font-normal leading-relaxed text-[rgb(var(--ala-dark))]/70"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5 }}
              className="relative flex flex-col gap-2 pb-10"
            >
              <div className="absolute -left-8 top-1.5 z-20">
                <div className="h-4 w-4 rounded-full border-[3px] border-[rgb(var(--ala-accent))] bg-[rgb(var(--ala-background))] shadow-md" />
              </div>
              <RichTextContent
                path="impactcontent.timeline_2_year"
                label="Timeline 2 Year"
                as="span"
                className="font-heading text-sm font-semibold uppercase tracking-[0.12em] text-[rgb(var(--ala-accent))]"
              />
              <RichTextContent
                path="impactcontent.timeline_2_title"
                label="Timeline 2 Title"
                as="h3"
                className="font-heading text-[clamp(19px,5vw,24px)] font-semibold leading-tight tracking-[-0.02em] text-[rgb(var(--ala-dark))]"
              />
              <RichTextContent
                path="impactcontent.timeline_2_body"
                label="Timeline 2 Body"
                as="p"
                className="max-w-[420px] font-heading text-base font-normal leading-relaxed text-[rgb(var(--ala-dark))]/70"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5 }}
              className="relative flex flex-col gap-2 pb-10"
            >
              <div className="absolute -left-8 top-1.5 z-20">
                <div className="h-4 w-4 rounded-full border-[3px] border-[rgb(var(--ala-accent))] bg-[rgb(var(--ala-background))] shadow-md" />
              </div>
              <RichTextContent
                path="impactcontent.timeline_3_year"
                label="Timeline 3 Year"
                as="span"
                className="font-heading text-sm font-semibold uppercase tracking-[0.12em] text-[rgb(var(--ala-accent))]"
              />
              <RichTextContent
                path="impactcontent.timeline_3_title"
                label="Timeline 3 Title"
                as="h3"
                className="font-heading text-[clamp(19px,5vw,24px)] font-semibold leading-tight tracking-[-0.02em] text-[rgb(var(--ala-dark))]"
              />
              <RichTextContent
                path="impactcontent.timeline_3_body"
                label="Timeline 3 Body"
                as="p"
                className="max-w-[420px] font-heading text-base font-normal leading-relaxed text-[rgb(var(--ala-dark))]/70"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5 }}
              className="relative flex flex-col gap-2 pb-10"
            >
              <div className="absolute -left-8 top-1.5 z-20">
                <div className="h-4 w-4 rounded-full border-[3px] border-[rgb(var(--ala-accent))] bg-[rgb(var(--ala-background))] shadow-md" />
              </div>
              <RichTextContent
                path="impactcontent.timeline_4_year"
                label="Timeline 4 Year"
                as="span"
                className="font-heading text-sm font-semibold uppercase tracking-[0.12em] text-[rgb(var(--ala-accent))]"
              />
              <RichTextContent
                path="impactcontent.timeline_4_title"
                label="Timeline 4 Title"
                as="h3"
                className="font-heading text-[clamp(19px,5vw,24px)] font-semibold leading-tight tracking-[-0.02em] text-[rgb(var(--ala-dark))]"
              />
              <RichTextContent
                path="impactcontent.timeline_4_body"
                label="Timeline 4 Body"
                as="p"
                className="max-w-[420px] font-heading text-base font-normal leading-relaxed text-[rgb(var(--ala-dark))]/70"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5 }}
              className="relative flex flex-col gap-2 pb-10"
            >
              <div className="absolute -left-8 top-1.5 z-20">
                <div className="h-4 w-4 rounded-full border-[3px] border-[rgb(var(--ala-accent))] bg-[rgb(var(--ala-background))] shadow-md" />
              </div>
              <RichTextContent
                path="impactcontent.timeline_5_year"
                label="Timeline 5 Year"
                as="span"
                className="font-heading text-sm font-semibold uppercase tracking-[0.12em] text-[rgb(var(--ala-accent))]"
              />
              <RichTextContent
                path="impactcontent.timeline_5_title"
                label="Timeline 5 Title"
                as="h3"
                className="font-heading text-[clamp(19px,5vw,24px)] font-semibold leading-tight tracking-[-0.02em] text-[rgb(var(--ala-dark))]"
              />
              <RichTextContent
                path="impactcontent.timeline_5_body"
                label="Timeline 5 Body"
                as="p"
                className="max-w-[420px] font-heading text-base font-normal leading-relaxed text-[rgb(var(--ala-dark))]/70"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Explore progress */}
      <section className="w-full overflow-clip bg-[rgb(var(--ala-dark))] py-20 md:py-32">
        <div className="mx-auto max-w-[1440px] px-6 md:px-[clamp(16px,6vw,87px)]">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mb-12 max-w-[720px] md:mb-16"
          >
            <div className="mb-4 flex items-center gap-3">
              <EyebrowDot light />
              <RichTextContent
                path="impactcontent.progress_eyebrow"
                label="Progress Eyebrow"
                as="span"
                className="font-heading text-base font-medium uppercase tracking-[2px] text-[rgb(var(--ala-on-primary))]/70"
              />
            </div>
            <RichTextContent
              path="impactcontent.progress_headline"
              label="Progress Headline"
              as="h2"
              className="font-heading text-[clamp(28px,3.2vw,48px)] font-medium leading-[1.1] tracking-[-0.03em] text-[rgb(var(--ala-on-primary))]"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative grid grid-cols-1 items-center gap-8 overflow-clip rounded-xl border border-white/10 bg-gradient-to-br from-white/[0.06] to-transparent p-8 md:p-12 lg:grid-cols-[1fr_auto]"
          >
            <div className="flex flex-col gap-4">
              <RichTextContent
                path="impactcontent.report_eyebrow"
                label="Report Eyebrow"
                as="span"
                className="font-heading text-sm font-semibold uppercase tracking-[0.15em] text-[rgb(var(--ala-accent))]"
              />
              <RichTextContent
                path="impactcontent.report_title"
                label="Report Title"
                as="h3"
                className="font-heading text-[clamp(30px,3.4vw,52px)] font-semibold leading-[1.05] tracking-[-0.02em] text-[rgb(var(--ala-on-primary))]"
              />
              <RichTextContent
                path="impactcontent.report_body"
                label="Report Body"
                as="p"
                className="max-w-[560px] font-heading text-[clamp(16px,1.3vw,20px)] font-normal leading-[1.55] text-[rgb(var(--ala-on-primary))]/70"
              />
              <EditableSlotGroup groupPath="impactcontent.report_cta" definition={reportCtaDef}>
                <a
                  href={reportCta.url || CSR_2026_PDF}
                  target={reportCta._target || '_blank'}
                  rel={reportCta._target === '_blank' ? 'noopener noreferrer' : undefined}
                  className="mt-2 inline-flex w-fit items-center gap-2 rounded-full bg-[rgb(var(--ala-accent))] px-7 py-4 font-heading text-sm font-semibold uppercase tracking-[-0.3px] text-[rgb(var(--ala-on-primary))] transition-colors hover:bg-[#B80022]"
                >
                  {reportCta.label || 'Read the 2026 Report'}
                  <ArrowRight className="h-4 w-4" />
                </a>
              </EditableSlotGroup>
            </div>
            <div className="hidden h-[260px] w-[200px] items-center justify-center rounded-lg border border-white/10 bg-white/5 lg:flex">
              <FileText className="h-20 w-20 text-[rgb(var(--ala-on-primary))]/30" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-8"
          >
            <RichTextContent
              path="impactcontent.archive_label"
              label="Archive Label"
              as="span"
              className="mb-4 block font-heading text-sm font-semibold uppercase tracking-[0.15em] text-[rgb(var(--ala-on-primary))]/50"
            />
            <div className="flex flex-wrap gap-3">
              <EditableSlotGroup groupPath="impactcontent.archive_1" definition={archive1Def}>
                <a
                  href={archive1.url || CSR_2025_PDF}
                  target={archive1._target || '_blank'}
                  rel={archive1._target === '_blank' ? 'noopener noreferrer' : undefined}
                  className="group flex items-center gap-3 rounded-lg border border-white/15 bg-white/[0.03] px-6 py-4 transition-colors hover:border-[rgb(var(--ala-accent))] hover:bg-white/[0.08]"
                >
                  <span className="font-heading text-[clamp(18px,1.6vw,24px)] font-semibold tracking-[-0.02em] text-[rgb(var(--ala-on-primary))]">
                    {archive1.label || 'Year'}
                  </span>
                  <ArrowRight className="h-4 w-4 text-[#c8c8c8] transition-all group-hover:translate-x-1 group-hover:text-[rgb(var(--ala-accent))]" />
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="impactcontent.archive_2" definition={archive2Def}>
                <a
                  href={archive2.url || CSR_2024_PDF}
                  target={archive2._target || '_blank'}
                  rel={archive2._target === '_blank' ? 'noopener noreferrer' : undefined}
                  className="group flex items-center gap-3 rounded-lg border border-white/15 bg-white/[0.03] px-6 py-4 transition-colors hover:border-[rgb(var(--ala-accent))] hover:bg-white/[0.08]"
                >
                  <span className="font-heading text-[clamp(18px,1.6vw,24px)] font-semibold tracking-[-0.02em] text-[rgb(var(--ala-on-primary))]">
                    {archive2.label || 'Year'}
                  </span>
                  <ArrowRight className="h-4 w-4 text-[#c8c8c8] transition-all group-hover:translate-x-1 group-hover:text-[rgb(var(--ala-accent))]" />
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="impactcontent.archive_3" definition={archive3Def}>
                <a
                  href={archive3.url || CSR_2023_PDF}
                  target={archive3._target || '_blank'}
                  rel={archive3._target === '_blank' ? 'noopener noreferrer' : undefined}
                  className="group flex items-center gap-3 rounded-lg border border-white/15 bg-white/[0.03] px-6 py-4 transition-colors hover:border-[rgb(var(--ala-accent))] hover:bg-white/[0.08]"
                >
                  <span className="font-heading text-[clamp(18px,1.6vw,24px)] font-semibold tracking-[-0.02em] text-[rgb(var(--ala-on-primary))]">
                    {archive3.label || 'Year'}
                  </span>
                  <ArrowRight className="h-4 w-4 text-[#c8c8c8] transition-all group-hover:translate-x-1 group-hover:text-[rgb(var(--ala-accent))]" />
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="impactcontent.archive_4" definition={archive4Def}>
                <a
                  href={archive4.url || CSR_2022_PDF}
                  target={archive4._target || '_blank'}
                  rel={archive4._target === '_blank' ? 'noopener noreferrer' : undefined}
                  className="group flex items-center gap-3 rounded-lg border border-white/15 bg-white/[0.03] px-6 py-4 transition-colors hover:border-[rgb(var(--ala-accent))] hover:bg-white/[0.08]"
                >
                  <span className="font-heading text-[clamp(18px,1.6vw,24px)] font-semibold tracking-[-0.02em] text-[rgb(var(--ala-on-primary))]">
                    {archive4.label || 'Year'}
                  </span>
                  <ArrowRight className="h-4 w-4 text-[#c8c8c8] transition-all group-hover:translate-x-1 group-hover:text-[rgb(var(--ala-accent))]" />
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="impactcontent.archive_5" definition={archive5Def}>
                <a
                  href={archive5.url || CSR_2021_PDF}
                  target={archive5._target || '_blank'}
                  rel={archive5._target === '_blank' ? 'noopener noreferrer' : undefined}
                  className="group flex items-center gap-3 rounded-lg border border-white/15 bg-white/[0.03] px-6 py-4 transition-colors hover:border-[rgb(var(--ala-accent))] hover:bg-white/[0.08]"
                >
                  <span className="font-heading text-[clamp(18px,1.6vw,24px)] font-semibold tracking-[-0.02em] text-[rgb(var(--ala-on-primary))]">
                    {archive5.label || 'Year'}
                  </span>
                  <ArrowRight className="h-4 w-4 text-[#c8c8c8] transition-all group-hover:translate-x-1 group-hover:text-[rgb(var(--ala-accent))]" />
                </a>
              </EditableSlotGroup>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Resources */}
      <section className="w-full overflow-clip bg-[#F7F7F7] py-20 md:py-32">
        <div className="mx-auto max-w-[1440px] px-6 md:px-[clamp(16px,6vw,87px)]">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mb-12 max-w-[720px] md:mb-16"
          >
            <div className="mb-4 flex items-center gap-3">
              <EyebrowDot />
              <RichTextContent
                path="impactcontent.resources_eyebrow"
                label="Resources Eyebrow"
                as="span"
                className="font-heading text-base font-medium uppercase tracking-[2px] text-[rgb(var(--ala-dark))]/60"
              />
            </div>
            <RichTextContent
              path="impactcontent.resources_headline"
              label="Resources Headline"
              as="h2"
              className="font-heading text-[clamp(28px,3.2vw,48px)] font-medium leading-[1.1] tracking-[-0.03em] text-[rgb(var(--ala-dark))]"
            />
          </motion.div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <EditableSlotGroup groupPath="impactcontent.resource_1_cta" definition={resource1Def}>
              <motion.a
                href={resource1.url || CSR_2026_PDF}
                target={resource1._target || '_blank'}
                rel={resource1._target === '_blank' ? 'noopener noreferrer' : undefined}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0 }}
                className="group flex flex-col gap-6 rounded-xl border border-[rgb(var(--ala-dark))]/10 bg-[rgb(var(--ala-background))] p-8 transition-all hover:border-[rgb(var(--ala-accent))] hover:shadow-lg"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[rgb(var(--ala-accent))]/10 transition-colors group-hover:bg-[rgb(var(--ala-accent))]">
                  <FileText className="h-7 w-7 text-[rgb(var(--ala-accent))] transition-colors group-hover:text-[rgb(var(--ala-on-primary))]" />
                </div>
                <div className="flex flex-1 flex-col gap-3">
                  <RichTextContent
                    path="impactcontent.resource_1_title"
                    label="Resource 1 Title"
                    as="h3"
                    className="font-heading text-[clamp(20px,1.8vw,26px)] font-semibold leading-tight tracking-[-0.02em] text-[rgb(var(--ala-dark))]"
                  />
                  <RichTextContent
                    path="impactcontent.resource_1_body"
                    label="Resource 1 Body"
                    as="p"
                    className="font-heading text-base font-normal leading-relaxed text-[rgb(var(--ala-dark))]/70"
                  />
                </div>
                <div className="inline-flex items-center gap-2 font-heading text-base font-semibold text-[rgb(var(--ala-accent))]">
                  {resource1.label || 'View Reports'}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </motion.a>
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="impactcontent.resource_2_cta" definition={resource2Def}>
              <motion.a
                href={resource2.url || ESG_POLICY_PDF}
                target={resource2._target || '_blank'}
                rel={resource2._target === '_blank' ? 'noopener noreferrer' : undefined}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.12 }}
                className="group flex flex-col gap-6 rounded-xl border border-[rgb(var(--ala-dark))]/10 bg-[rgb(var(--ala-background))] p-8 transition-all hover:border-[rgb(var(--ala-accent))] hover:shadow-lg"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[rgb(var(--ala-accent))]/10 transition-colors group-hover:bg-[rgb(var(--ala-accent))]">
                  <ShieldCheck className="h-7 w-7 text-[rgb(var(--ala-accent))] transition-colors group-hover:text-[rgb(var(--ala-on-primary))]" />
                </div>
                <div className="flex flex-1 flex-col gap-3">
                  <RichTextContent
                    path="impactcontent.resource_2_title"
                    label="Resource 2 Title"
                    as="h3"
                    className="font-heading text-[clamp(20px,1.8vw,26px)] font-semibold leading-tight tracking-[-0.02em] text-[rgb(var(--ala-dark))]"
                  />
                  <RichTextContent
                    path="impactcontent.resource_2_body"
                    label="Resource 2 Body"
                    as="p"
                    className="font-heading text-base font-normal leading-relaxed text-[rgb(var(--ala-dark))]/70"
                  />
                </div>
                <div className="inline-flex items-center gap-2 font-heading text-base font-semibold text-[rgb(var(--ala-accent))]">
                  {resource2.label || 'Read the Policy'}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </motion.a>
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="impactcontent.resource_3_cta" definition={resource3Def}>
              <motion.a
                href={resource3.url || SUSTAINABLE_LIVING_GUIDE_PDF}
                target={resource3._target || '_blank'}
                rel={resource3._target === '_blank' ? 'noopener noreferrer' : undefined}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.24 }}
                className="group flex flex-col gap-6 rounded-xl border border-[rgb(var(--ala-dark))]/10 bg-[rgb(var(--ala-background))] p-8 transition-all hover:border-[rgb(var(--ala-accent))] hover:shadow-lg"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[rgb(var(--ala-accent))]/10 transition-colors group-hover:bg-[rgb(var(--ala-accent))]">
                  <Leaf className="h-7 w-7 text-[rgb(var(--ala-accent))] transition-colors group-hover:text-[rgb(var(--ala-on-primary))]" />
                </div>
                <div className="flex flex-1 flex-col gap-3">
                  <RichTextContent
                    path="impactcontent.resource_3_title"
                    label="Resource 3 Title"
                    as="h3"
                    className="font-heading text-[clamp(20px,1.8vw,26px)] font-semibold leading-tight tracking-[-0.02em] text-[rgb(var(--ala-dark))]"
                  />
                  <RichTextContent
                    path="impactcontent.resource_3_body"
                    label="Resource 3 Body"
                    as="p"
                    className="font-heading text-base font-normal leading-relaxed text-[rgb(var(--ala-dark))]/70"
                  />
                </div>
                <div className="inline-flex items-center gap-2 font-heading text-base font-semibold text-[rgb(var(--ala-accent))]">
                  {resource3.label || 'Open the Guide'}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </motion.a>
            </EditableSlotGroup>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="relative w-full overflow-clip bg-[rgb(var(--ala-dark))] py-24 md:py-40">
        <div className="pointer-events-none absolute inset-0 opacity-[0.06]">
          <div className="absolute left-1/2 top-1/2 h-[700px] w-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[rgb(var(--ala-accent))] blur-[160px]" />
        </div>
        <div className="relative z-10 mx-auto flex max-w-[1440px] flex-col items-center px-6 text-center md:px-[clamp(16px,6vw,87px)]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex flex-col items-center gap-8"
          >
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-[4px] bg-[rgb(var(--ala-accent))]" aria-hidden />
              <RichTextContent
                path="impactcontent.cta_eyebrow"
                label="CTA Eyebrow"
                as="span"
                className="font-heading text-base font-medium uppercase tracking-[2px] text-[rgb(var(--ala-on-primary))]/60"
              />
            </div>
            <RichTextContent
              path="impactcontent.cta_headline"
              label="CTA Headline"
              as="h2"
              className="max-w-[1000px] font-heading text-[clamp(36px,5.5vw,84px)] font-semibold leading-[1.02] tracking-[-0.03em] text-[rgb(var(--ala-on-primary))]"
            />
            <RichTextContent
              path="impactcontent.cta_body"
              label="CTA Body"
              as="p"
              className="max-w-[640px] font-heading text-[clamp(18px,1.6vw,24px)] font-normal leading-[1.5] text-[rgb(var(--ala-on-primary))]/70"
            />
            <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
              <EditableSlotGroup
                groupPath="impactcontent.communities_cta"
                definition={communitiesCtaDef}
              >
                <a
                  href={communitiesCta.url || '/our-communities'}
                  target={communitiesCta._target || '_self'}
                  className="group inline-flex w-fit items-center justify-center gap-2 rounded-full bg-[rgb(var(--ala-secondary))] px-5 py-[15px] font-heading text-sm font-semibold uppercase tracking-[-0.3px] text-[rgb(var(--ala-on-secondary))] transition-colors hover:bg-[#3a85d0]"
                >
                  {communitiesCta.label || 'Explore Our Communities'}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="impactcontent.about_cta" definition={aboutCtaDef}>
                <a
                  href={aboutCta.url || '/about-us'}
                  target={aboutCta._target || '_self'}
                  className="group inline-flex w-fit items-center justify-center gap-2 rounded-full px-5 py-[15px] font-heading text-sm font-semibold uppercase tracking-[-0.3px] text-[rgb(var(--ala-on-primary))] shadow-[inset_0_0_0_2px_rgb(var(--ala-secondary))] transition-colors hover:bg-[rgb(var(--ala-secondary))] hover:text-[rgb(var(--ala-on-secondary))]"
                >
                  {aboutCta.label || 'Learn About American Landmark'}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </a>
              </EditableSlotGroup>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Static schema anchors for ESG accordion dynamic paths */}
      <div className="hidden" aria-hidden>
        <RichTextContent path="impactcontent.env_label" label="Environmental Label" as="span" />
        <RichTextContent path="impactcontent.env_tagline" label="Environmental Tagline" as="span" />
        <RichTextContent path="impactcontent.env_body" label="Environmental Body" as="span" />
        <RichTextContent path="impactcontent.env_tags" label="Environmental Tags" as="span" />
        <RichTextContent path="impactcontent.social_label" label="Social Label" as="span" />
        <RichTextContent path="impactcontent.social_tagline" label="Social Tagline" as="span" />
        <RichTextContent path="impactcontent.social_body" label="Social Body" as="span" />
        <RichTextContent path="impactcontent.social_tags" label="Social Tags" as="span" />
        <RichTextContent path="impactcontent.gov_label" label="Governance Label" as="span" />
        <RichTextContent path="impactcontent.gov_tagline" label="Governance Tagline" as="span" />
        <RichTextContent path="impactcontent.gov_body" label="Governance Body" as="span" />
        <RichTextContent path="impactcontent.gov_tags" label="Governance Tags" as="span" />
      </div>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ImpactContent',
  displayName: 'Impact Content',
  tokens: {
    'impactcontent.stats_eyebrow': {
      label: 'Stats Eyebrow',
      type: 'richtext',
      defaultValue: '<p>impact at a glance</p>',
    },
    'impactcontent.stats_headline': {
      label: 'Stats Headline',
      type: 'richtext',
      defaultValue: '<p>Tangible impact, measured in lives and communities.</p>',
    },
    'impactcontent.stat_1_value': {
      label: 'Stat 1 Value',
      type: 'richtext',
      defaultValue: '<p>32+</p>',
    },
    'impactcontent.stat_1_label': {
      label: 'Stat 1 Label',
      type: 'richtext',
      defaultValue: '<p>Artists hosted through Artist in Residence</p>',
    },
    'impactcontent.stat_2_value': {
      label: 'Stat 2 Value',
      type: 'richtext',
      defaultValue: '<p>3,000+</p>',
    },
    'impactcontent.stat_2_label': {
      label: 'Stat 2 Label',
      type: 'richtext',
      defaultValue: '<p>Resident events facilitated</p>',
    },
    'impactcontent.stat_3_value': {
      label: 'Stat 3 Value',
      type: 'richtext',
      defaultValue: '<p>5</p>',
    },
    'impactcontent.stat_3_label': {
      label: 'Stat 3 Label',
      type: 'richtext',
      defaultValue: '<p>UN Sustainable Development Goals aligned</p>',
    },
    'impactcontent.stat_4_value': {
      label: 'Stat 4 Value',
      type: 'richtext',
      defaultValue: '<p>2026</p>',
    },
    'impactcontent.stat_4_label': {
      label: 'Stat 4 Label',
      type: 'richtext',
      defaultValue: '<p>Latest Corporate Responsibility Report</p>',
    },
    'impactcontent.esg_eyebrow': {
      label: 'ESG Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Responsible Investment</p>',
    },
    'impactcontent.esg_headline': {
      label: 'ESG Headline',
      type: 'richtext',
      defaultValue: '<p>Three pillars. One commitment to responsible growth.</p>',
    },
    'impactcontent.esg_body': {
      label: 'ESG Body',
      type: 'richtext',
      defaultValue:
        '<p>ESG is the framework behind our broader impact story—environmental stewardship, social responsibility, and governance working together.</p>',
    },
    'impactcontent.env_label': {
      label: 'Environmental Label',
      type: 'richtext',
      defaultValue: '<p>Environmental</p>',
    },
    'impactcontent.env_tagline': {
      label: 'Environmental Tagline',
      type: 'richtext',
      defaultValue: '<p>Better buildings. Smarter resources.</p>',
    },
    'impactcontent.env_body': {
      label: 'Environmental Body',
      type: 'richtext',
      defaultValue:
        '<p>We are committed to protecting our planet through sustainable practices that allow us to reduce our environmental footprint. Our focus on best practices enhances our residents’ experience and conserves precious natural resources. We look to save water by replacing toilets, shower heads, faucets and water-intensive landscaping. We improve energy efficiency through light fixtures, smart thermostats and dimmers and Energy Star appliances. We are expanding our efficiency protocols to include solar energy projects and electric vehicle charging stations. We do this because it’s the right thing to do. We also know it makes good business sense as generating value through well-run operations benefits our residents and all of our stakeholders.</p>',
    },
    'impactcontent.env_tags': {
      label: 'Environmental Tags',
      type: 'richtext',
      defaultValue:
        '<p>Energy efficiency|Water conservation|Solar initiatives|EV charging|Efficient appliances|Sustainable landscaping|Resiliency</p>',
    },
    'impactcontent.social_label': {
      label: 'Social Label',
      type: 'richtext',
      defaultValue: '<p>Social</p>',
    },
    'impactcontent.social_tagline': {
      label: 'Social Tagline',
      type: 'richtext',
      defaultValue: '<p>People and communities at the center.</p>',
    },
    'impactcontent.social_body': {
      label: 'Social Body',
      type: 'richtext',
      defaultValue:
        '<p>We know that when our team succeeds, we excel as a company and our stakeholders benefit directly. And we know that when our residents thrive, we are achieving our mission. So, we work hard to make our company and our multifamily communities inclusive places for everyone - no exceptions – where we embrace, respect and value different viewpoints, backgrounds and capabilities. To show our commitment we join leading limited partners and general partners to implement and measure best practices as an Institutional Limited Partners Association Diversity in Action Signatory. And we strive to create healthy, equitable and sustainable environments where residents and the broader community can flourish.</p>',
    },
    'impactcontent.social_tags': {
      label: 'Social Tags',
      type: 'richtext',
      defaultValue:
        '<p>Resident wellness|Community engagement|Artist in Residence|Educator in Residence|Diversity &amp; inclusion|Professional development|Philanthropy|Housing accessibility</p>',
    },
    'impactcontent.gov_label': {
      label: 'Governance Label',
      type: 'richtext',
      defaultValue: '<p>Governance</p>',
    },
    'impactcontent.gov_tagline': {
      label: 'Governance Tagline',
      type: 'richtext',
      defaultValue: '<p>Accountability that builds trust.</p>',
    },
    'impactcontent.gov_body': {
      label: 'Governance Body',
      type: 'richtext',
      defaultValue:
        "<p>Doing the right thing doesn't just happen. Our leadership makes it happen. By implementing governance policies and practices that align our corporate decision-making with our objectives and values, our leadership turns their good governance vision into touchstones everyone can count on – Our Mission, Ethos and Values and comprehensive policies and practices that create transparency and set high standards. Our leadership models these standards every day. Integrity. Understanding. Equity and Diversity. Environmental Stewardship. Responsibility. Excellence. Unity.</p>",
    },
    'impactcontent.gov_tags': {
      label: 'Governance Tags',
      type: 'richtext',
      defaultValue:
        '<p>ESG governance|Leadership oversight|Ethics|Transparency|Risk management|ESG KPIs|Human rights|Responsible investment</p>',
    },
    'impactcontent.focus_eyebrow': {
      label: 'Focus Eyebrow',
      type: 'richtext',
      defaultValue: '<p>people / planet / performance</p>',
    },
    'impactcontent.focus_headline': {
      label: 'Focus Headline',
      type: 'richtext',
      defaultValue: '<p>Where responsibility meets strategy.</p>',
    },
    'impactcontent.people_label': {
      label: 'People Label',
      type: 'richtext',
      defaultValue: '<p>People</p>',
    },
    'impactcontent.people_headline': {
      label: 'People Headline',
      type: 'richtext',
      defaultValue: '<p>Helping people thrive.</p>',
    },
    'impactcontent.people_body': {
      label: 'People Body',
      type: 'richtext',
      defaultValue:
        '<p>Residents. Team members. Artists. Educators. Communities. We invest in the people who make our communities vibrant places to live and grow.</p>',
    },
    'impactcontent.planet_label': {
      label: 'Planet Label',
      type: 'richtext',
      defaultValue: '<p>Planet</p>',
    },
    'impactcontent.planet_headline': {
      label: 'Planet Headline',
      type: 'richtext',
      defaultValue: '<p>Building more sustainable communities.</p>',
    },
    'impactcontent.planet_body': {
      label: 'Planet Body',
      type: 'richtext',
      defaultValue:
        '<p>Energy. Water. Efficiency. Resiliency. Smarter buildings and operations that reduce our footprint while improving the resident experience.</p>',
    },
    'impactcontent.performance_label': {
      label: 'Performance Label',
      type: 'richtext',
      defaultValue: '<p>Performance</p>',
    },
    'impactcontent.performance_headline': {
      label: 'Performance Headline',
      type: 'richtext',
      defaultValue: '<p>Creating long-term value.</p>',
    },
    'impactcontent.performance_body': {
      label: 'Performance Body',
      type: 'richtext',
      defaultValue:
        '<p>Responsible investment. Risk management. Operational performance. Discipline that delivers durable returns for our capital partners.</p>',
    },
    'impactcontent.timeline_eyebrow': {
      label: 'Timeline Eyebrow',
      type: 'richtext',
      defaultValue: '<p>our commitment in action</p>',
    },
    'impactcontent.timeline_headline': {
      label: 'Timeline Headline',
      type: 'richtext',
      defaultValue: '<p>A timeline of momentum.</p>',
    },
    'impactcontent.timeline_body': {
      label: 'Timeline Body',
      type: 'richtext',
      defaultValue:
        '<p>Our impact initiatives continue to grow—year over year, community by community.</p>',
    },
    'impactcontent.timeline_1_year': {
      label: 'Timeline 1 Year',
      type: 'richtext',
      defaultValue: '<p>2020</p>',
    },
    'impactcontent.timeline_1_title': {
      label: 'Timeline 1 Title',
      type: 'richtext',
      defaultValue: '<p>Artist in Residence launches</p>',
    },
    'impactcontent.timeline_1_body': {
      label: 'Timeline 1 Body',
      type: 'richtext',
      defaultValue:
        '<p>A bold idea takes shape—bringing art and creativity directly into the communities we call home.</p>',
    },
    'impactcontent.timeline_2_year': {
      label: 'Timeline 2 Year',
      type: 'richtext',
      defaultValue: '<p>2022</p>',
    },
    'impactcontent.timeline_2_title': {
      label: 'Timeline 2 Title',
      type: 'richtext',
      defaultValue: '<p>ESG initiatives &amp; reporting expand</p>',
    },
    'impactcontent.timeline_2_body': {
      label: 'Timeline 2 Body',
      type: 'richtext',
      defaultValue:
        '<p>We formalize our environmental, social, and governance framework and begin transparent reporting.</p>',
    },
    'impactcontent.timeline_3_year': {
      label: 'Timeline 3 Year',
      type: 'richtext',
      defaultValue: '<p>2023–2025</p>',
    },
    'impactcontent.timeline_3_title': {
      label: 'Timeline 3 Title',
      type: 'richtext',
      defaultValue: '<p>Environmental &amp; community programs grow</p>',
    },
    'impactcontent.timeline_3_body': {
      label: 'Timeline 3 Body',
      type: 'richtext',
      defaultValue:
        '<p>Solar, water conservation, resident programming, and philanthropy scale across our portfolio.</p>',
    },
    'impactcontent.timeline_4_year': {
      label: 'Timeline 4 Year',
      type: 'richtext',
      defaultValue: '<p>2025</p>',
    },
    'impactcontent.timeline_4_title': {
      label: 'Timeline 4 Title',
      type: 'richtext',
      defaultValue: '<p>Educator in Residence pilot launches</p>',
    },
    'impactcontent.timeline_4_body': {
      label: 'Timeline 4 Body',
      type: 'richtext',
      defaultValue:
        '<p>We extend our residency model to educators—bringing tutoring, literacy, and career resources home.</p>',
    },
    'impactcontent.timeline_5_year': {
      label: 'Timeline 5 Year',
      type: 'richtext',
      defaultValue: '<p>2026</p>',
    },
    'impactcontent.timeline_5_title': {
      label: 'Timeline 5 Title',
      type: 'richtext',
      defaultValue: '<p>Latest Corporate Responsibility Report</p>',
    },
    'impactcontent.timeline_5_body': {
      label: 'Timeline 5 Body',
      type: 'richtext',
      defaultValue:
        '<p>Our most comprehensive look yet at progress across people, planet, and performance.</p>',
    },
    'impactcontent.progress_eyebrow': {
      label: 'Progress Eyebrow',
      type: 'richtext',
      defaultValue: '<p>explore our progress</p>',
    },
    'impactcontent.progress_headline': {
      label: 'Progress Headline',
      type: 'richtext',
      defaultValue: '<p>Explore Our Progress</p>',
    },
    'impactcontent.report_eyebrow': {
      label: 'Report Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Latest Report</p>',
    },
    'impactcontent.report_title': {
      label: 'Report Title',
      type: 'richtext',
      defaultValue: '<p>2026 Corporate Responsibility Report</p>',
    },
    'impactcontent.report_body': {
      label: 'Report Body',
      type: 'richtext',
      defaultValue:
        "<p>Explore American Landmark's latest progress across environmental, social, and governance initiatives.</p>",
    },
    'impactcontent.archive_label': {
      label: 'Archive Label',
      type: 'richtext',
      defaultValue: '<p>Archive</p>',
    },
    'impactcontent.resources_eyebrow': {
      label: 'Resources Eyebrow',
      type: 'richtext',
      defaultValue: '<p>resources</p>',
    },
    'impactcontent.resources_headline': {
      label: 'Resources Headline',
      type: 'richtext',
      defaultValue: '<p>Go deeper.</p>',
    },
    'impactcontent.resource_1_title': {
      label: 'Resource 1 Title',
      type: 'richtext',
      defaultValue: '<p>Corporate Responsibility Reports</p>',
    },
    'impactcontent.resource_1_body': {
      label: 'Resource 1 Body',
      type: 'richtext',
      defaultValue:
        '<p>Explore our progress and priorities across environmental, social, and governance initiatives.</p>',
    },
    'impactcontent.resource_2_title': {
      label: 'Resource 2 Title',
      type: 'richtext',
      defaultValue: '<p>ESG Policy</p>',
    },
    'impactcontent.resource_2_body': {
      label: 'Resource 2 Body',
      type: 'richtext',
      defaultValue:
        '<p>Read the framework guiding our ESG program and responsible investment practices.</p>',
    },
    'impactcontent.resource_3_title': {
      label: 'Resource 3 Title',
      type: 'richtext',
      defaultValue: '<p>Sustainable Living Guide</p>',
    },
    'impactcontent.resource_3_body': {
      label: 'Resource 3 Body',
      type: 'richtext',
      defaultValue:
        '<p>Explore ways residents can make more sustainable choices in their everyday lives.</p>',
    },
    'impactcontent.cta_eyebrow': {
      label: 'CTA Eyebrow',
      type: 'richtext',
      defaultValue: '<p>our impact</p>',
    },
    'impactcontent.cta_headline': {
      label: 'CTA Headline',
      type: 'richtext',
      defaultValue: "<p>Better communities don't happen by accident.</p>",
    },
    'impactcontent.cta_body': {
      label: 'CTA Body',
      type: 'richtext',
      defaultValue:
        '<p>They happen when people, purpose, and performance come together.</p>',
    },
  },
  slotGroups: {
    'impactcontent.people_image': peopleImageDef,
    'impactcontent.planet_image': planetImageDef,
    'impactcontent.performance_image': performanceImageDef,
    'impactcontent.report_cta': reportCtaDef,
    'impactcontent.archive_1': archive1Def,
    'impactcontent.archive_2': archive2Def,
    'impactcontent.archive_3': archive3Def,
    'impactcontent.archive_4': archive4Def,
    'impactcontent.archive_5': archive5Def,
    'impactcontent.resource_1_cta': resource1Def,
    'impactcontent.resource_2_cta': resource2Def,
    'impactcontent.resource_3_cta': resource3Def,
    'impactcontent.communities_cta': communitiesCtaDef,
    'impactcontent.about_cta': aboutCtaDef,
  },
};

(ImpactContent as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default ImpactContent;
